import { generateCaptchaToken } from '@worldcoo/ui/lib/captcha';
import { useTranslation } from '@/lib/i18n';
import { postContactFormOptions } from '@/lib/queries';
import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import { useAppForm } from '@worldcoo/ui/components/form';
import { cn } from '@worldcoo/ui/lib/utils';
import { toast } from 'sonner';
import * as z from 'zod';

export function Newsletter({ className }: { className?: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { mutateAsync: postContactForm, isSuccess } = useMutation(postContactFormOptions());

	const contactSchema = z.object({
		email: z.email(t`Invalid email`),
	});

	const form = useAppForm({
		defaultValues: {
			email: '',
		},
		validators: { onSubmit: contactSchema },
		onSubmit: async ({ value }) => {
			const captchaToken = await generateCaptchaToken();
			const result = await postContactForm({
				email: value.email,
				category: 'newsletter',
				message: 'Newsletter subscription',
				name: '',
				phone: '',
				checkbox: {
					type: 'termsAndConditions',
					checked: true,
					text: '',
					label: '',
				},
				captchaToken,
				language,
			});

			if (!result.captchaPassed) {
				toast.error(t`Captcha verification failed. Please try again.`);
			} else {
				toast.success(t`You have successfully subscribed to the newsletter!`);
			}
		},
	});

	return (
		<section className={cn('bg-hero py-16 md:py-24', className)}>
			<div className="mx-auto flex max-w-4xl flex-col items-center gap-8 px-8 text-center">
				<h2 className="text-3xl font-semibold md:text-5xl">{t`Small donations for big causes`}</h2>
				<p className="text-lg">{t`Sign up to our newsletter to keep up to speed with all our projects`}</p>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="flex w-full max-w-md flex-col gap-4 sm:flex-row"
				>
					<form.AppField
						name="email"
						children={(field) => (
							<field.TextField type="email" label={t`Email address`} variant="background" className="md:min-w-64" />
						)}
					/>
					<form.AppForm>
						<form.SubmitButton size="xl" label={t`Sign up`} disabled={isSuccess} />
					</form.AppForm>
				</form>
			</div>
		</section>
	);
}
